<script setup lang="ts">
import { useTranslate } from '@tolgee/vue'

interface Props {
  label?: string
  disabled?: boolean
}

type ColorModes = 'rgb' | 'rgba' | 'hsl' | 'hsla' | 'hex' | 'hexa'

defineProps<Props>()
const model = defineModel({ default: '#FFFFFF' })

const { t } = useTranslate('local')

const allowedColorModes: ColorModes[] = ['hex', 'rgb']
</script>

<template>
  <div class="color-picker">
    <label class="color-picker-label" v-if="label">{{ label }}</label>
    <v-text-field
      v-model="model"
      flat
      variant="outlined"
      readonly
      :disabled="disabled"
      :placeholder="t('colorPicker.color', { color: model })"
    >
      <template v-slot:prepend>
        <v-menu location="top" offset="15" :close-on-content-click="false" close-on-back>
          <template v-slot:activator="{ props }">
            <div class="color-picker-swatch" :style="{ backgroundColor: model }" v-bind="props">
              <span class="sr-only">{{ model }}</span>
            </div>
          </template>
          <v-card>
            <v-card-text class="pa-0">
              <v-color-picker v-model="model" :modes="allowedColorModes" />
            </v-card-text>
          </v-card>
        </v-menu>
      </template>
    </v-text-field>
  </div>
</template>

<style lang="scss" scoped>
.color-picker {
  width: 100%;

  :deep(.v-field) {
    border: 0;
  }

  :deep(.v-field__outline) {
    --v-field-border-width: 0px;
  }

  :deep(.v-field__input) {
    padding-left: 0;
  }

  :deep(.v-input) {
    border: 1px solid var(--gray-light-cd);
    border-radius: 10px;
  }

  :deep(.v-input__prepend) {
    margin-right: 10px;
    padding-left: 16px;
  }
}

.color-picker-label {
  display: block;
  font-size: 16px;
  margin-bottom: 8px;
}

.color-picker-swatch {
  cursor: pointer;
  height: 20px;
  width: 20px;
  border: 1px solid var(--gray-light-cd);
}
</style>
